/**
 * Landing footer (lighter forest band) + floating deadline popup.
 *
 * Footer link targets: everything that maps to an app surface goes to /login
 * (the visitor is logged out on this page); legal/company pages don't exist
 * yet and render as non-links.
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { AlarmClock, ArrowRight, X } from 'lucide-react';
import { BrandLogo } from '@finmin/ui';
import { CHANNEL_PARTNER_LOGIN, FOOTER_COLUMNS, ITR_DEADLINE_ISO } from './data';
import { useCountdown } from './use-countdown';

export function LandingFooter() {
  return (
    <footer className="relative bg-forest">
      {/* Lighten the band relative to the final CTA section, as in the design */}
      <div className="pointer-events-none absolute inset-0 bg-cream/[0.06]" aria-hidden />

      <div className="relative mx-auto max-w-7xl px-4 pb-8 pt-14 sm:px-6">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-3 lg:grid-cols-6">
          {/* Brand blurb — centered on mobile per the mobile Figma */}
          <div className="col-span-2 flex flex-col items-center text-center sm:items-start sm:text-left">
            <BrandLogo wordColor="cream" height={28} />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-hero-muted-foreground">
              India&apos;s 1st Financial Operating System. One System. From Income to Wealth.
            </p>
          </div>

          {FOOTER_COLUMNS.map((col) => (
            <div key={col.heading}>
              <p className="text-xs font-bold uppercase tracking-widest text-cream">
                {col.heading}
              </p>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((label) => (
                  <li key={label}>
                    <Link
                      to="/login"
                      // "Login as Channel Partner" signs in and lands on the CP page.
                      search={label === CHANNEL_PARTNER_LOGIN ? { as: 'channel-partner' } : {}}
                      className="text-sm text-hero-muted-foreground transition-colors hover:text-cream"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-hero-border/40 pt-6 text-center text-xs text-hero-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:text-left">
          <p>© 2025 FinminOS. All rights reserved. India&apos;s 1st Financial Operating System.</p>
          <p>Investments are subject to market risk. Please read all scheme-related documents carefully.</p>
        </div>
      </div>
    </footer>
  );
}

/**
 * Floating deadline popup — the Figma's "FLOATING POPUP ANNOUNCEMENT".
 * Fixed to the bottom of the viewport; dismissible for the session.
 */
export function FloatingDeadlinePopup() {
  const [dismissed, setDismissed] = React.useState(
    () => sessionStorage.getItem('finmin.landing.popup.dismissed') === '1',
  );
  const { daysLeft, expired } = useCountdown(ITR_DEADLINE_ISO);

  if (dismissed || expired) return null;

  function dismiss() {
    setDismissed(true);
    try {
      sessionStorage.setItem('finmin.landing.popup.dismissed', '1');
    } catch {
      // non-fatal
    }
  }

  return (
    <div className="fixed inset-x-3 bottom-3 z-50 sm:inset-x-auto sm:left-1/2 sm:w-full sm:max-w-[680px] sm:-translate-x-1/2">
      <div className="flex items-center gap-3 rounded-[16px] border border-amber/35 bg-forest-deep px-4 py-3 shadow-[0_16px_48px_rgba(0,0,0,0.35)]">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber/15"
          aria-hidden
        >
          <AlarmClock className="h-4 w-4 text-amber" />
        </span>
        <p className="flex-1 text-xs leading-relaxed text-cream sm:text-sm">
          Only <span className="font-bold text-amber">{daysLeft} days</span> left to file your
          ITR — Miss the deadline and pay a ₹5,000 penalty and lose the opportunity to claim
          all possible deductions.
        </p>
        <Link
          to="/login"
          className="hidden shrink-0 items-center gap-1.5 rounded-lg bg-amber px-4 py-2 text-xs font-semibold text-white transition-all hover:brightness-95 active:scale-95 sm:inline-flex"
        >
          Start Filing
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="shrink-0 rounded-md p-1 text-cream/60 transition-colors hover:text-cream"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
